import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environment';
import {
  ExternalIdentity,
  PassengerProfile,
  PassengerProfileListMeta,
  PassengerProfileListQuery,
  PassengerProfileListResponse
} from '../models/passenger-profile.model';

interface ApiListResponse {
  data?: any[];
  Data?: any[];
  meta?: Partial<PassengerProfileListMeta>;
  Meta?: Partial<PassengerProfileListMeta>;
}

interface ApiDetailResponse {
  data?: any;
  Data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class PassengerProfileService {
  private apiUrl = `${environment.apiUrl}/passenger-profiles`;

  constructor(private http: HttpClient) {}

  getPassengerProfiles(query: PassengerProfileListQuery): Observable<PassengerProfileListResponse> {
    let params = new HttpParams()
      .set('page', query.page)
      .set('perPage', query.perPage);

    if (query.search?.trim()) {
      params = params.set('search', query.search.trim());
    }

    if (query.source) {
      params = params.set('source', query.source);
    }

    return this.http.get<ApiListResponse>(this.apiUrl, { params }).pipe(
      map(response => {
        const profiles = this.extractArray(this.pickValue(response, ['data', 'Data']));
        const meta = this.pickValue(response, ['meta', 'Meta']) || {};

        return {
          data: profiles.map(profile => this.mapProfile(profile)),
          meta: {
            page: Number(this.pickValue(meta, ['page', 'Page']) || query.page),
            perPage: Number(this.pickValue(meta, ['perPage', 'PerPage']) || query.perPage),
            total: Number(this.pickValue(meta, ['total', 'Total']) || 0),
            totalPages: Number(this.pickValue(meta, ['totalPages', 'TotalPages']) || 1)
          }
        };
      })
    );
  }

  getPassengerProfile(id: string): Observable<PassengerProfile> {
    return this.http.get<ApiDetailResponse>(`${this.apiUrl}/${id}`).pipe(
      map(response => this.mapProfile(this.pickValue(response, ['data', 'Data']) || response))
    );
  }

  private mapProfile(profile: any): PassengerProfile {
    const externalIdentities = this.extractArray(this.pickValue(profile, ['externalIdentities', 'ExternalIdentities']));

    return {
      id: String(this.pickValue(profile, ['id', 'ID', 'profileId', 'ProfileID', 'passengerProfileId', 'passengerProfileID', 'PassengerProfileID']) ?? ''),
      displayName: this.pickValue(profile, ['displayName', 'DisplayName', 'fullName', 'FullName', 'name', 'Name']) ?? 'Unknown passenger',
      firstName: this.pickValue(profile, ['firstName', 'FirstName']),
      lastName: this.pickValue(profile, ['lastName', 'LastName']),
      phone: this.pickValue(profile, ['phone', 'Phone', 'phoneNumber', 'PhoneNumber']),
      email: this.pickValue(profile, ['email', 'Email', 'emailAddress', 'EmailAddress']),
      primarySource: this.pickValue(profile, ['primarySource', 'PrimarySource', 'source', 'Source']) ?? 'Unknown',
      linkedAppUser: this.pickValue(profile, ['linkedAppUser', 'LinkedAppUser', 'appUser', 'AppUser']),
      firstSeen: this.pickValue(profile, ['firstSeen', 'FirstSeen', 'firstSeenAt', 'FirstSeenAt', 'createdAt', 'CreatedAt', 'createdDate', 'CreatedDate']),
      lastSeen: this.pickValue(profile, ['lastSeen', 'LastSeen', 'lastSeenAt', 'LastSeenAt']),
      updatedDate: this.pickValue(profile, ['updatedDate', 'UpdatedDate', 'updatedAt', 'UpdatedAt', 'modifiedAt', 'ModifiedAt']),
      externalIdentities: externalIdentities.map((identity: any) => this.mapExternalIdentity(identity)),
      notes: this.pickValue(profile, ['notes', 'Notes'])
    };
  }

  private mapExternalIdentity(identity: any): ExternalIdentity {
    return {
      id: String(this.pickValue(identity, ['id', 'ID', 'externalIdentityId', 'externalIdentityID', 'ExternalIdentityID', 'passengerExternalIdentityID', 'PassengerExternalIdentityID', 'identityId', 'IdentityID']) ?? ''),
      provider: this.pickValue(identity, ['provider', 'Provider']) ?? 'Powerlynx',
      entityType: this.pickValue(identity, ['entityType', 'EntityType', 'externalEntityType', 'ExternalEntityType', 'type', 'Type']) ?? 'Customer',
      externalId: String(this.pickValue(identity, ['externalId', 'externalID', 'ExternalID', 'providerEntityId', 'ProviderEntityID', 'providerId', 'ProviderID']) ?? ''),
      status: this.pickValue(identity, ['status', 'Status']) ?? 'Unknown',
      onlineState: this.pickValue(identity, ['onlineState', 'OnlineState', 'isOnlineState', 'IsOnlineState'])
        ?? (this.pickValue(identity, ['isOnline', 'IsOnline']) === true ? 'Online' : this.pickValue(identity, ['isOnline', 'IsOnline']) === false ? 'Offline' : 'Unknown'),
      lastOnline: this.pickValue(identity, ['lastOnline', 'LastOnline', 'lastOnlineAt', 'LastOnlineAt']),
      firstSynced: this.pickValue(identity, ['firstSynced', 'FirstSynced', 'firstSyncedAt', 'FirstSyncedAt', 'createdAt', 'CreatedAt']),
      lastSynced: this.pickValue(identity, ['lastSynced', 'LastSynced', 'lastSyncedAt', 'LastSyncedAt', 'updatedAt', 'UpdatedAt']),
      phone: this.pickValue(identity, ['phone', 'Phone', 'phoneNumber', 'PhoneNumber']),
      email: this.pickValue(identity, ['email', 'Email', 'emailAddress', 'EmailAddress']),
      sourcePhone: this.pickValue(identity, ['sourcePhone', 'SourcePhone', 'sourcePhoneNumber', 'SourcePhoneNumber']),
      sourceEmail: this.pickValue(identity, ['sourceEmail', 'SourceEmail', 'sourceEmailAddress', 'SourceEmailAddress'])
    };
  }

  private extractArray(value: any): any[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (Array.isArray(value?.$values)) {
      return value.$values;
    }

    return [];
  }

  private pickValue(source: any, keys: string[]): any {
    if (!source || typeof source !== 'object') {
      return undefined;
    }

    for (const key of keys) {
      const value = source[key];
      if (value !== undefined && value !== null) {
        return value;
      }
    }

    return undefined;
  }
}
